import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, ViewChild, OnInit, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

/** Above PrimeNG dialog stack (mask 9999 / dialog ~10001). */
const CALENDAR_OVERLAY_Z_INDEX = 11002;
const CALENDAR_ESTIMATED_HEIGHT_PX = 360;
const DEFAULT_MIN_YEAR = 1970;

@Component({
  selector: 'lib-charts-date-range-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './charts-date-range-picker.component.html',
  styleUrl: './charts-date-range-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChartsDateRangePickerComponent),
      multi: true
    }
  ]
})
export class ChartsDateRangePickerComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy, OnChanges {
  /** Ensures only one calendar overlay is open (prevents body-portaled panels blocking other fields). */
  private static openPicker: ChartsDateRangePickerComponent | null = null;
  @Input() placeholder: string = 'Choose Date Range';
  @Input() styleClass: string = '';
  @Input() disabled: boolean = false;
  @Input() showIcon: boolean = true;
  @Input() dateFormat: string = 'dd/mm/yy';
  @Input() singleDateMode: boolean = false; // For single date selection
  @Input() minDate: Date | null = null; // Minimum selectable date
  @Input() maxDate: Date | null = null; // Maximum selectable date (defaults to today to disable future dates)
  /**
   * If true, future dates are disabled (default behavior for filters).
   * If false, future dates are allowed (needed for admin expiry selection).
   */
  @Input() disableFutureDates: boolean = true;
  /** When true, calendar dropdown opens above the input (for top fields to avoid overlapping fields below) */
  @Input() openAbove: boolean = false;
  /** When true, calendar is positioned over the trigger input instead of above/below it */
  @Input() overlayOnTrigger = false;
  /** When true, calendar expands under the trigger in-flow so the placeholder stays visible. */
  @Input() inline = false;
  /** When true, emit onDateRangeChange after the start date is picked (before end date). */
  @Input() emitPartialSelection = false;

  @Output() onDateRangeChange = new EventEmitter<DateRange>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  dateRange: DateRange = { startDate: null, endDate: null };
  isOpen = false;
  currentMonth: Date = new Date();
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  hoverDate: Date | null = null;
  private isToggling = false; // Flag to prevent document click from interfering during toggle
  private dropdownOriginalParent: HTMLElement | null = null;
  private scrollListener: (() => void) | null = null;
  
  @ViewChild('calendarDropdown', { static: false }) calendarDropdown!: ElementRef<HTMLDivElement>;
  @ViewChild('triggerElement', { static: false }) triggerElement!: ElementRef<HTMLDivElement>;

  readonly monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  
  private onChange = (value: DateRange | null) => {};
  private onTouched = () => {};

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  private _docClickHandler = (event: MouseEvent): void => {
    this.onDocumentClick(event);
  };

  ngOnInit(): void {
    document.addEventListener('click', this._docClickHandler, { capture: true });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disableFutureDates']) {
      this.syncMaxDateConstraint();
    }
  }

  ngAfterViewInit(): void {
    this.syncMaxDateConstraint();
  }

  private syncMaxDateConstraint(): void {
    if (!this.disableFutureDates) {
      this.maxDate = null;
      return;
    }
    if (this.maxDate === null) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      this.maxDate = today;
    }
  }

  /** Capture phase so outside-click runs before sibling trigger stopPropagation prevents bubble to document. */
  onDocumentClick(event: MouseEvent): void {
    if (this.isToggling) {
      return;
    }

    if (!this.isOpen) {
      return;
    }

    const triggerEl = this.triggerElement?.nativeElement;
    const dropdownEl = this.calendarDropdown?.nativeElement;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

    const isInside =
      path.some((node) => {
        if (!(node instanceof Node)) {
          return false;
        }
        if (node === triggerEl || node === dropdownEl) {
          return true;
        }
        return (
          (triggerEl?.contains(node) ?? false) ||
          (dropdownEl?.contains(node) ?? false) ||
          this.elementRef.nativeElement.contains(node)
        );
      });

    if (!isInside) {
      this.moveDropdownBackAndClose();
    }
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.moveDropdownBackAndClose();
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._docClickHandler, { capture: true });
    this.detachCalendarOverlay(true);
    if (ChartsDateRangePickerComponent.openPicker === this) {
      ChartsDateRangePickerComponent.openPicker = null;
    }
  }

  private detachCalendarOverlay(removeFromDom = false): void {
    const el = this.calendarDropdown?.nativeElement;
    if (el) {
      el.classList.remove('date-range-picker-calendar-overlay');
      if (el.parentNode === document.body) {
        if (removeFromDom) {
          el.remove();
        } else if (this.dropdownOriginalParent) {
          this.dropdownOriginalParent.appendChild(el);
        }
      }
    }
    this.dropdownOriginalParent = null;
    this.removeScrollListener();
    if (this.isOpen) {
      this.isOpen = false;
      this.onClose.emit();
      this.cdr.markForCheck();
    }
  }

  private moveDropdownBackAndClose(): void {
    this.detachCalendarOverlay(false);
    if (ChartsDateRangePickerComponent.openPicker === this) {
      ChartsDateRangePickerComponent.openPicker = null;
    }
  }

  private notifyDateRangeChange(dateRange: DateRange, defer = false): void {
    const emit = () => this.onDateRangeChange.emit(dateRange);
    if (defer) {
      queueMicrotask(emit);
      return;
    }
    emit();
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      window.removeEventListener('resize', this.scrollListener);
      this.scrollListener = null;
    }
  }

  // Removed maxDate limitation to allow future date selection

  get displayValue(): string {
    if (this.singleDateMode) {
      return this.dateRange.startDate ? this.formatDate(this.dateRange.startDate) : '';
    }
    if (this.dateRange.startDate && this.dateRange.endDate) {
      return this.formatDateRange(this.dateRange.startDate, this.dateRange.endDate);
    } else if (this.dateRange.startDate) {
      return this.formatDate(this.dateRange.startDate) + ' - ...';
    }
    return '';
  }

  formatDate(date: Date | null | undefined): string {
    if (!date) {
      return '';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatDateForButton(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatDateRange(start: Date | null | undefined, end: Date | null | undefined): string {
    return `${this.formatDate(start)} to ${this.formatDate(end)}`;
  }

  toggleDropdown(event?: MouseEvent): void {
    if (!this.disabled) {
      // Prevent event from bubbling to document click handler
      if (event) {
        event.stopPropagation();
      }
      
      // Set flag to prevent document click from interfering
      this.isToggling = true;
      
      const wasOpen = this.isOpen;
      this.isOpen = !this.isOpen;
      
      if (this.isOpen && !wasOpen) {
        if (
          ChartsDateRangePickerComponent.openPicker &&
          ChartsDateRangePickerComponent.openPicker !== this
        ) {
          ChartsDateRangePickerComponent.openPicker.moveDropdownBackAndClose();
        }
        ChartsDateRangePickerComponent.openPicker = this;
        this.onTouched();
        this.onOpen.emit();
        // Initialize selection state
        this.selectedStartDate = this.dateRange.startDate;
        this.selectedEndDate = this.dateRange.endDate;
        
        // If no date is selected and minDate is set, show the month of minDate
        if (!this.dateRange.startDate && this.minDate) {
          this.currentMonth = new Date(this.minDate);
          this.currentMonth.setDate(1); // Set to first day of month
        } else if (!this.dateRange.startDate && !this.minDate) {
          // If no date and no minDate, show current month
          this.currentMonth = new Date();
          this.currentMonth.setDate(1);
        } else if (this.dateRange.startDate) {
          // If date is selected, show the month of the selected date
          this.currentMonth = new Date(this.dateRange.startDate);
          this.currentMonth.setDate(1);
        }
        
        this.cdr.detectChanges();

        // Render calendar, then portal to body (avoids modal overflow:hidden clipping).
        setTimeout(() => {
          if (this.isOpen && this.calendarDropdown && !this.inline) {
            this.positionCalendarDropdown();
          }
          setTimeout(() => {
            this.isToggling = false;
            this.cdr.markForCheck();
          }, 300);
        }, 0);
      } else if (!this.isOpen && wasOpen) {
        this.moveDropdownBackAndClose();
        // Reset flag immediately when closing
        setTimeout(() => {
          this.isToggling = false;
        }, 0);
      } else {
        // Reset flag if no state change
        setTimeout(() => {
          this.isToggling = false;
        }, 0);
      }
    }
  }

  private positionCalendarDropdown(): void {
    if (this.inline || !this.calendarDropdown || !this.triggerElement) {
      return;
    }

    const trigger = this.triggerElement.nativeElement;
    const dropdown = this.calendarDropdown.nativeElement;
    const triggerRect = trigger.getBoundingClientRect();
    const viewportW = window.innerWidth || document.documentElement.clientWidth;

    // Append to body so calendar is not clipped by modal/table overflow:hidden.
    if (dropdown.parentNode !== document.body) {
      this.dropdownOriginalParent = dropdown.parentElement;
      document.body.appendChild(dropdown);
    }

    dropdown.classList.add('date-range-picker-calendar-overlay');
    if (this.overlayOnTrigger) {
      dropdown.classList.add('date-range-picker-calendar-overlay--on-trigger');
    } else {
      dropdown.classList.remove('date-range-picker-calendar-overlay--on-trigger');
    }

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const placeAbove = !this.overlayOnTrigger && (
      (this.openAbove && spaceAbove >= CALENDAR_ESTIMATED_HEIGHT_PX) ||
      (spaceBelow < CALENDAR_ESTIMATED_HEIGHT_PX && spaceAbove > spaceBelow)
    );

    const width = Math.min(340, Math.max(320, triggerRect.width));
    let left = triggerRect.left;
    if (left + width > viewportW - 8) {
      left = Math.max(8, viewportW - width - 8);
    }

    dropdown.style.position = 'fixed';
    dropdown.style.left = `${left}px`;
    dropdown.style.width = `${width}px`;
    dropdown.style.minWidth = `${width}px`;
    dropdown.style.maxWidth = `${width}px`;
    dropdown.style.marginTop = '0';
    dropdown.style.marginBottom = '0';
    dropdown.style.zIndex = String(CALENDAR_OVERLAY_Z_INDEX);
    dropdown.style.pointerEvents = 'auto';

    if (this.overlayOnTrigger) {
      dropdown.style.bottom = 'auto';
      dropdown.style.top = `${triggerRect.top}px`;
    } else if (placeAbove) {
      dropdown.style.top = 'auto';
      dropdown.style.bottom = `${window.innerHeight - triggerRect.top + 8}px`;
    } else {
      dropdown.style.bottom = 'auto';
      dropdown.style.top = `${triggerRect.bottom + 8}px`;
    }

    this.removeScrollListener();
    this.scrollListener = () => {
      if (this.isOpen && this.triggerElement) {
        this.positionCalendarDropdown();
      }
    };
    window.addEventListener('scroll', this.scrollListener, true);
    window.addEventListener('resize', this.scrollListener, { passive: true });
  }

  getDaysInMonth(date: Date): Date[] {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Date[] = [];
    
    // Get the day of week (0 = Sunday, 1 = Monday, etc.)
    // Adjust to start week on Monday (0 = Monday, 6 = Sunday)
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6; // Sunday becomes 6
    
    // Add days from previous month to fill the week (starting from Monday)
    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push(prevDate);
    }
    
    // Add days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    // Add days from next month to fill the week
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push(new Date(year, month + 1, day));
    }
    
    return days;
  }

  getMonthName(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  get availableYears(): number[] {
    const today = new Date();
    const minYear = this.minDate?.getFullYear() ?? DEFAULT_MIN_YEAR;
    const maxYear = this.disableFutureDates
      ? (this.maxDate?.getFullYear() ?? today.getFullYear())
      : (this.maxDate?.getFullYear() ?? today.getFullYear() + 10);
    const years: number[] = [];
    for (let y = maxYear; y >= minYear; y--) {
      years.push(y);
    }
    return years;
  }

  onMonthSelectValue(month: number): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), month, 1);
    this.cdr.detectChanges();
    if (this.isOpen) {
      setTimeout(() => this.positionCalendarDropdown(), 0);
    }
  }

  onYearSelectValue(year: number): void {
    this.currentMonth = new Date(year, this.currentMonth.getMonth(), 1);
    this.cdr.detectChanges();
    if (this.isOpen) {
      setTimeout(() => this.positionCalendarDropdown(), 0);
    }
  }

  getWeekDays(): string[] {
    return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  isPastDate(date: Date): boolean {
    // This method is kept for backward compatibility but now enables past dates
    // Use isFutureDate() or isDateDisabled() instead
    return false;
  }

  isFutureDate(date: Date): boolean {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    // If future dates are allowed, none are considered "future disabled"
    if (!this.disableFutureDates) {
      return false;
    }

    // Get max date (defaults to today if not set)
    const maxDate = this.maxDate || new Date();
    const maxDateCheck = new Date(maxDate);
    maxDateCheck.setHours(23, 59, 59, 999); // End of the day
    
    // Disable dates after maxDate (future dates)
    if (checkDate > maxDateCheck) {
      return true;
    }
    
    return false;
  }

  isDateDisabled(date: Date): boolean {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    // Disable dates before minDate (if set)
    if (this.minDate) {
      const minDateCheck = new Date(this.minDate);
      minDateCheck.setHours(0, 0, 0, 0);
      if (checkDate < minDateCheck) {
        return true;
      }
    }
    
    // Disable dates after maxDate (future dates) only when disableFutureDates=true
    if (this.disableFutureDates) {
      const maxDate = this.maxDate || new Date();
      const maxDateCheck = new Date(maxDate);
      maxDateCheck.setHours(23, 59, 59, 999);
      if (checkDate > maxDateCheck) {
        return true;
      }
    }
    
    return false;
  }
  
  isBeforeMinDate(date: Date): boolean {
    if (!this.minDate) return false;
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const minDateCheck = new Date(this.minDate);
    minDateCheck.setHours(0, 0, 0, 0);
    return checkDate < minDateCheck;
  }

  isInRange(date: Date): boolean {
    // Check preview mode (hovering)
    if (this.selectedStartDate && this.hoverDate && !this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.hoverDate.getTime();
      const current = date.getTime();
      return current >= Math.min(start, end) && current <= Math.max(start, end);
    }
    
    // Check complete range
    if (this.selectedStartDate && this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.selectedEndDate.getTime();
      const current = date.getTime();
      return current >= start && current <= end;
    }
    
    return false;
  }

  isStartDate(date: Date): boolean {
    if (!this.selectedStartDate) return false;
    return date.toDateString() === this.selectedStartDate.toDateString();
  }

  isEndDate(date: Date): boolean {
    if (!this.selectedEndDate) return false;
    return date.toDateString() === this.selectedEndDate.toDateString();
  }

  isOtherMonth(date: Date): boolean {
    return date.getMonth() !== this.currentMonth.getMonth();
  }

  selectDate(date: Date, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.isDateDisabled(date)) return;
    
    // Single date mode - close immediately after selection
    if (this.singleDateMode) {
      this.selectedStartDate = new Date(date);
      this.selectedEndDate = null;
      this.hoverDate = null;
      
      this.dateRange = {
        startDate: this.selectedStartDate,
        endDate: null
      };
      this.onChange(this.dateRange);
      this.moveDropdownBackAndClose();
      this.cdr.detectChanges();
      this.notifyDateRangeChange(this.dateRange);
      return;
    }
    
    // Range mode - original behavior
    if (!this.selectedStartDate || (this.selectedStartDate && this.selectedEndDate)) {
      // Start new selection
      this.selectedStartDate = new Date(date);
      this.selectedEndDate = null;
      this.hoverDate = null;
      
      // Update the model with just start date (don't emit yet - wait for end date)
      this.dateRange = {
        startDate: this.selectedStartDate,
        endDate: null
      };
      this.onChange(this.dateRange);
      if (this.emitPartialSelection) {
        this.notifyDateRangeChange(this.dateRange, true);
      }
    } else if (this.selectedStartDate && !this.selectedEndDate) {
      // Complete the range
      if (date < this.selectedStartDate) {
        // If selected date is before start, swap them
        this.selectedEndDate = new Date(this.selectedStartDate);
        this.selectedStartDate = new Date(date);
      } else {
        this.selectedEndDate = new Date(date);
      }
      
      // Update the model
      this.dateRange = {
        startDate: this.selectedStartDate,
        endDate: this.selectedEndDate
      };
      this.onChange(this.dateRange);
      // Close before notifying parent so a re-render does not leave a body-portaled overlay behind
      this.moveDropdownBackAndClose();
      this.cdr.detectChanges();
      this.notifyDateRangeChange(this.dateRange);
    }
  }

  onDateHover(date: Date): void {
    if (this.selectedStartDate && !this.selectedEndDate) {
      this.hoverDate = new Date(date);
    }
  }

  onDateLeave(): void {
    this.hoverDate = null;
  }

  canNavigateToPreviousMonth(): boolean {
    if (!this.minDate) {
      return true;
    }
    const minMonth = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
    const prevMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    return prevMonth >= minMonth;
  }

  previousMonth(event?: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.canNavigateToPreviousMonth()) {
      return;
    }
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.cdr.detectChanges();
  }

  canNavigateToNextMonth(): boolean {
    // If future dates are allowed, allow navigation freely
    if (!this.disableFutureDates) {
      return true;
    }

    const maxDate = this.maxDate || new Date();
    const nextMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    // Check if the first day of next month is before or equal to maxDate
    return nextMonth <= maxDate;
  }

  nextMonth(event?: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.canNavigateToNextMonth()) {
      return;
    }
    const next = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.currentMonth = next;
    this.cdr.detectChanges();
  }

  clear(): void {
    // Close the calendar if it's open before clearing
    const wasOpen = this.isOpen;
    if (wasOpen) {
      this.moveDropdownBackAndClose();
    }
    
    // Clear the date range
    this.dateRange = { startDate: null, endDate: null };
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.hoverDate = null;
    this.onChange(null);
    this.onDateRangeChange.emit({ startDate: null, endDate: null });
    this.onTouched();
    
    // Reset toggling flag
    this.isToggling = false;
  }

  /**
   * Handle clear icon click - prevent event propagation and clear the date range
   */
  onClearClick(event: MouseEvent): void {
    // Stop propagation to prevent triggering toggleDropdown
    event.stopPropagation();
    event.preventDefault();
    
    // Clear the date range (this will also close the calendar if open)
    this.clear();
  }

  writeValue(value: DateRange | null): void {
    // Don't reset isOpen state when writeValue is called - it's only for updating the date range value
    if (value && value.startDate) {
      this.dateRange = {
        startDate: new Date(value.startDate),
        endDate: value.endDate ? new Date(value.endDate) : null
      };
      this.selectedStartDate = new Date(value.startDate);
      this.selectedEndDate = value.endDate ? new Date(value.endDate) : null;
      // Preserve the month the user navigated to while the calendar is open
      if (!this.isOpen) {
        this.currentMonth = new Date(value.startDate);
        this.currentMonth.setDate(1);
      }
    } else {
      this.dateRange = { startDate: null, endDate: null };
      this.selectedStartDate = null;
      this.selectedEndDate = null;
    }
  }

  registerOnChange(fn: (value: DateRange | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  trackByDate(index: number, date: Date): number {
    return date.getTime();
  }

  isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  }

  isStartOfRange(date: Date): boolean {
    if (this.selectedStartDate && date.toDateString() === this.selectedStartDate.toDateString()) {
      return true;
    }
    // Also check if this is the start in preview mode
    if (this.selectedStartDate && this.hoverDate && !this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.hoverDate.getTime();
      const current = date.getTime();
      return current === Math.min(start, end);
    }
    return false;
  }

  isEndOfRange(date: Date): boolean {
    if (this.selectedEndDate && date.toDateString() === this.selectedEndDate.toDateString()) {
      return true;
    }
    // Also check if this is the end in preview mode
    if (this.selectedStartDate && this.hoverDate && !this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.hoverDate.getTime();
      const current = date.getTime();
      return current === Math.max(start, end);
    }
    return false;
  }

  isInRangeButNotStartOrEnd(date: Date): boolean {
    // Check if we're in preview mode (start selected, hovering for end)
    if (this.selectedStartDate && this.hoverDate && !this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.hoverDate.getTime();
      const current = date.getTime();
      const minTime = Math.min(start, end);
      const maxTime = Math.max(start, end);
      const isStart = date.toDateString() === this.selectedStartDate.toDateString();
      const isEnd = date.toDateString() === this.hoverDate.toDateString();
      return current > minTime && current < maxTime && !isStart && !isEnd;
    }
    
    // Check if we have a complete range
    if (this.selectedStartDate && this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.selectedEndDate.getTime();
      const current = date.getTime();
      return current > start && current < end;
    }
    
    return false;
  }

  getDateClasses(date: Date): string {
    const baseClasses = 'w-9 h-9 flex items-center justify-center text-sm font-medium transition-all cursor-pointer relative border-0 outline-none focus:outline-none';
    
    if (this.isDateDisabled(date)) {
      return `${baseClasses} text-gray-300 cursor-not-allowed opacity-50 bg-transparent`;
    }
    
    if (this.isOtherMonth(date)) {
      return `${baseClasses} text-gray-300 hover:bg-gray-50 rounded-lg bg-transparent`;
    }
    
    const isWeekend = this.isWeekend(date);
    const isStart = this.isStartOfRange(date);
    const isEnd = this.isEndOfRange(date);
    const isInRange = this.isInRangeButNotStartOrEnd(date);
    const isToday = this.isToday(date);
    
    // Start date - dark green circular
    if (isStart) {
      return `${baseClasses} bg-teal-700 text-white font-semibold rounded-full z-10`;
    }
    
    // End date - dark green circular
    if (isEnd) {
      return `${baseClasses} bg-teal-700 text-white font-semibold rounded-full z-10`;
    }
    
    // Dates in range (between start and end) - rectangular background
    if (isInRange) {
      if (isWeekend) {
        // Weekend in range - slightly darker light green
        return `${baseClasses} bg-green-200 text-gray-900 font-normal`;
      }
      // Weekdays in range - light green background, rectangular
      return `${baseClasses} bg-green-100 text-gray-900 font-normal`;
    }
    
    // Today - light green circular (only if not selected or in range)
    if (isToday) {
      return `${baseClasses} bg-green-300 text-gray-900 font-semibold rounded-full`;
    }
    
    // Weekend dates - no special background when not selected
    if (isWeekend) {
      return `${baseClasses} text-gray-900 hover:bg-green-50 rounded-lg bg-transparent`;
    }
    
    // Regular weekdays - no background
    return `${baseClasses} text-gray-900 hover:bg-green-50 rounded-lg bg-transparent`;
  }
}
